import React from 'react';
import CSVReader from 'react-csv-reader';
import { useSelector } from 'react-redux';
import saveAs from 'file-saver';
import Papa from 'papaparse';
import { Box, Switch, Backdrop, DotProgress, Typography, Button, Grid, color } from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { selectors as clientsSelectors, Client } from 'store/clients';
import { selectors as kiosksSelectors, actions as kiosksActions, Kiosk, KioskScheduleEntry } from 'store/kiosks';
import { KioskHardware } from 'store/kioskHardware';
import { tryToParseObject } from 'utils/stringFormat';
import packageJSON from '../../../package.json';
import KioskTable, { sort } from './components/KioskTable';
import { useStyles } from './styled';

const KiosksView: React.FC = () => {
  const { getKiosks, resetKiosk, checkAppVersion, updateAutoRefresh, updateKiosks, resetExportData } = useActions({
    getKiosks: kiosksActions.getKiosks,
    resetKiosk: kiosksActions.resetKiosk,
    checkAppVersion: kiosksActions.checkAppVersion,
    updateAutoRefresh: kiosksActions.updateAutoRefresh,
    updateKiosks: kiosksActions.updateKiosks,
    resetExportData: kiosksActions.resetExportData
  });
  const client = useSelector(clientsSelectors.selectClient) as Client;
  const kiosks = useSelector(kiosksSelectors.selectKiosks);
  const appVersion = useSelector(kiosksSelectors.selectAppVersion);
  const autoRefresh = useSelector(kiosksSelectors.selectAutoRefresh);
  const message = useSelector(kiosksSelectors.selectMessage);
  const exportData = useSelector(kiosksSelectors.selectExportData);
  const exportPending = useSelector(kiosksSelectors.selectExportPending);
  const { pending: kiosksPending } = useSelector(kiosksSelectors.selectAction);
  const stockCountsByPrinter = useSelector(kiosksSelectors.selectStockCountsByPrinter);

  const [sortParams, setSortParams] = React.useState({ column: 'name', order: 'asc' });

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const classes = useStyles()();

  // ON UPDATE OF CLIENT
  React.useEffect(() => {
    resetKiosk();
    checkAppVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  // ON VERSION CHANGE: RELOAD PAGE
  React.useEffect(() => {
    if (packageJSON.version && appVersion && packageJSON.version !== appVersion) {
      window.location.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appVersion]);

  // ON EXPORT DATA FETCHED: WRITE TO CSV
  React.useEffect(() => {
    if (exportData && exportData.length > 0) {
      const kiosksToShow = exportData.filter((k: Kiosk) => k.configuration && k.configuration.showInKCC);
      const sortedData = sort(kiosksToShow, stockCountsByPrinter, sortParams.column, sortParams.order);
      const formattedData = sortedData.map((k: Kiosk) => {
        const extractedSchedule =
          k.schedule &&
          k.schedule.map((entry: KioskScheduleEntry) => ({
            day: entry.day,
            startTime: entry.startTime,
            endTime: entry.endTime
          }));
        const formattedSchedule = JSON.stringify(extractedSchedule);

        // Add stock counts of printers, depending on the includeInKiosksExport flag
        const printerStockCountsForKiosk = [...stockCountsByPrinter.keys()].reduce((acc, printerName) => {
          const kioskPrinter = k.hardware
            .filter((h) => h.type === 'printer')
            .find((h) =>
              printerName.length ? h.configuration.displayName === printerName : !h.configuration.displayName
            );
          if (kioskPrinter && kioskPrinter.configuration.includeInKiosksExport) {
            return {
              ...acc,
              [`${printerName} Remaining`.trim()]: stockCountsByPrinter.get(printerName)?.[k.id] || ''
            };
          }
          return acc;
        }, {});

        return {
          'Kiosk ID': k.id,
          'Kiosk Name': k.name,
          ...printerStockCountsForKiosk,
          Schedule: formattedSchedule
        };
      });

      const csvData = Papa.unparse(formattedData);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, 'Kiosks.csv');

      resetExportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  // TOGGLE THE AUTO REFRESH
  const setAutoRefresh = (checked: boolean) => {
    updateAutoRefresh(checked);
  };

  // UPDATE KIOSKS USING IMPORTED FILE
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFileUpload = (data: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const importedData = data.map((item: any) => {
      const kioskId = item['Kiosk ID'];

      // Add updated stock counts to the kioskHardware of the payload
      const kioskPrinters = (kiosks.find((k) => k.id === kioskId)?.hardware || []).filter((h) => h.type === 'printer');
      const updatedKioskHardware: Partial<KioskHardware>[] = [];
      [...stockCountsByPrinter.keys()].forEach((printerName) => {
        const kioskPrinter = kioskPrinters.find((kp) =>
          printerName ? kp.configuration.displayName === printerName : !kp.configuration.displayName
        );
        const updatedCurrentStock = item[`${printerName} Remaining`.trim()];
        if (kioskPrinter && updatedCurrentStock) {
          updatedKioskHardware.push({
            id: kioskPrinter.id,
            values: { currentStock: Number(updatedCurrentStock) }
          });
        }
      });

      // verify the schedule is in JSON format
      const scheduleArray = tryToParseObject(item.Schedule);
      // if not, setting to null will cause the API to fail
      if (!scheduleArray) return null;

      return {
        kioskId,
        kioskName: item['Kiosk Name'],
        hardware: updatedKioskHardware,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        schedule: scheduleArray.map((obj: any) => ({
          day: obj.day,
          startTime: obj.startTime,
          endTime: obj.endTime
        }))
      };
    });
    // call API to update all kiosks
    updateKiosks(importedData);
    // reset the imported data
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // OPEN FILE EXPLORER
  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <>
      {kiosksPending && (
        <Backdrop sx={{ zIndex: '2147483647', opacity: '0.3' }} open={kiosksPending ? true : false}>
          <DotProgress color={color.LIGHT_GRAY} />
        </Backdrop>
      )}
      {/* HEADER ROW */}
      <Grid className={classes.kiosksHeaderBlock}>
        <Box flexOverride="none">
          <Typography className={classes.kiosksHeading}>Kiosks</Typography>
        </Box>
        <div style={{ display: 'inline-flex', justifyContent: 'space-evenly' }}>
          {/* IMPORT BUTTON */}
          <Grid style={{ marginRight: '20px' }}>
            <Button customSize="small" width={200} onClick={handleClick} data-testid="import-button">
              Import from .csv
              <CSVReader
                ref={fileInputRef}
                onFileLoaded={handleFileUpload}
                parserOptions={{
                  header: true,
                  skipEmptyLines: true
                }}
                inputStyle={{ display: 'none' }}
              />
            </Button>
          </Grid>
          {/* EXPORT BUTTON */}
          <Grid style={{ marginRight: '20px' }}>
            <Button
              customSize="small"
              width={200}
              onClick={() => getKiosks({ exportData: true })}
              isLoading={exportPending ? true : false}
              disabled={kiosks.length <= 0}
              data-testid="export-button"
            >
              Export to .csv
            </Button>
          </Grid>
          {/* AUTO REFRESH TOGGLE */}
          <Grid className={classes.autoRefreshContainer}>
            <Typography className={classes.autoRefreshHeading}>Auto Refresh</Typography>
            <Box sx={{ width: '130px' }}>
              <Switch
                checked={autoRefresh}
                onChange={(_e, checked) => setAutoRefresh(checked)}
                data-testid="kiosk-autorefresh-switch"
                checkedLabel="Enabled"
                uncheckedLabel="Disabled"
              />
            </Box>
          </Grid>
        </div>
      </Grid>
      {/* TABLE OF KIOSK RECORDS */}
      <KioskTable pending={kiosksPending} sortParams={sortParams} setSortParams={setSortParams} message={message} />
    </>
  );
};

export default KiosksView;
